import {DoppeDtoBymoLinkViewerData} from "../../client-server-common/types/doppe-dto-bymo-link-viewer-data";
import React, {useContext} from "react";
import {assertNotNullable, Values} from "@wix/devzai-utils-common";
import {
    IBymoPagePreviewHostServiceApiClient
} from "../../client/bymo-page-preview-service/bymo-page-preview-host-service-api";
import {DoppeViewerInvocationSource, DoppeViewerPagesData} from "../../client-server-common";

export const BymoPageViewerMode = {
    /**
     * The page is rendered in an iframe inside the app without editing/interaction capabilities.
     * You can think of it as a screenshot of the page.
     */
    Preview: 'Preview',

    /**
     * The page is rendered in an iframe inside the app with editing/interaction capabilities.
     */
    LivePreview: 'LivePreview', // TODO: Consider to rename to `InteractivePreview`

    /**
     * Used when the viewer is rendered normally on the viewer/user's domain.
     * This is the standard mode for end-users visiting the page.
     */
    Live: 'Live',

    /**
     * Used when the viewer is rendered normally but on the app's domain (wix.com) for a real preview purpose.
     */
    LiveInApp: 'LiveInApp',
} as const;

export type BymoPageViewerMode = Values<typeof BymoPageViewerMode>;

export type BymoPageViewerLiveMode = typeof BymoPageViewerMode.Live | typeof BymoPageViewerMode.LiveInApp;

export type BymoPageViewerContextData =
    | BymoPageViewerContextData.LivePage
    | BymoPageViewerContextData.LivePageInApp
    | BymoPageViewerContextData.PagePreview
    | BymoPageViewerContextData.InteractivePagePreview
;

export type BymoPageViewerLivePageContextData = Extract<
    BymoPageViewerContextData,
    BymoPageViewerContextData.LivePage | BymoPageViewerContextData.LivePageInApp
>;

export namespace BymoPageViewerContextData {
    type BaseType<T extends BymoPageViewerMode> = {
        mode: T;
    }

    export type LivePage = BaseType<typeof BymoPageViewerMode.Live> & DoppeDtoBymoLinkViewerData.LivePageData;
    export type LivePageInApp = BaseType<typeof BymoPageViewerMode.LiveInApp> & DoppeDtoBymoLinkViewerData.LivePageData;
    export type PagePreview = BaseType<typeof BymoPageViewerMode.Preview>;
    export type InteractivePagePreview = BaseType<typeof BymoPageViewerMode.LivePreview> & {
        bymoPagePreviewHostServiceApiClient: IBymoPagePreviewHostServiceApiClient;
    };
}

export const BymoPageViewerContext = React.createContext<BymoPageViewerContextData | null>(null);

export function useBymoPageViewerContext () {
    return assertNotNullable(useContext(BymoPageViewerContext));
}

export function bymoPageViewerModeIsPreview (viewerMode: BymoPageViewerMode) {
    return viewerMode === BymoPageViewerMode.Preview || viewerMode === BymoPageViewerMode.LivePreview;
}

export function bymoPageViewerContextDataIsLivePage (viewerContextData: BymoPageViewerContextData) : viewerContextData is BymoPageViewerLivePageContextData {
    return (
        viewerContextData.mode === BymoPageViewerMode.Live ||
        viewerContextData.mode === BymoPageViewerMode.LiveInApp
    );
}

export function bymoPageViewerModeResolveFromViewerPageData (viewerBymoPageData: DoppeViewerPagesData.BymoPage) {
    if (viewerBymoPageData.invocationSource === DoppeViewerInvocationSource.DoppeApp) {
        return BymoPageViewerMode.LiveInApp;
    } else {
        return BymoPageViewerMode.Live;
    }
}